/**
 * LiveCallPopup — global bottom-right overlay shown when an Asterisk AI
 * voice call is in progress. Hidden when no calls are active.
 *
 * Driven by useVoiceEvents(): a WebSocket push from /ws/voice-events that
 * rehydrates from /api/voice-ai/active-calls on connect and on each
 * call.ringing/call.ended event (replaces the old 3s polling). Auto-opens
 * when the first active call appears, auto-closes when the last one ends.
 * Multiple simultaneous calls stack as separate cards. Globally killable
 * via the server-side VOICE_SCREEN_POP_ENABLED flag.
 */
import { useEffect, useState } from 'react';
import { Link } from 'wouter';
import { PhoneCall, X, User, Calendar, Package, Clock, ExternalLink, Globe } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useVoiceEvents, type ActiveCall } from '@/hooks/useVoiceEvents';

function StageBadge({ stage }: { stage: ActiveCall['stage'] }) {
  const m: Record<string, { label: string; cls: string }> = {
    'event-week': { label: 'Event week', cls: 'bg-rose-500/20 text-rose-300 border-rose-500/40' },
    'booked': { label: 'Booked', cls: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40' },
    'past_client': { label: 'Past client', cls: 'bg-slate-500/20 text-slate-300 border-slate-500/40' },
    'quoted': { label: 'Quoted', cls: 'bg-amber-500/20 text-amber-300 border-amber-500/40' },
    'lead': { label: 'Lead', cls: 'bg-sky-500/20 text-sky-300 border-sky-500/40' },
    'unknown': { label: 'Unknown', cls: 'bg-slate-700/40 text-slate-400 border-slate-600' },
  };
  const x = m[stage || 'unknown'] || m.unknown;
  return <Badge className={`${x.cls} border text-[10px]`}>{x.label}</Badge>;
}

function routeHint(stage: ActiveCall['stage']): string {
  switch (stage) {
    case 'event-week': return 'John (104)';
    case 'booked': return 'Abhiram (103)';
    case 'past_client':
    case 'quoted':
    case 'lead': return 'Renju (102)';
    default: return 'menu';
  }
}

function formatElapsed(seconds: number): string {
  if (seconds < 0) seconds = 0;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function formatCallerNumber(num: string): string {
  const clean = (num || '').replace(/^\+91/, '');
  if (clean.length === 10) return `${clean.slice(0, 5)} ${clean.slice(5)}`;
  return num || '';
}

function formatWeddingDate(dateStr: string | null): string {
  if (!dateStr) return '';
  try {
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-IN', {
      weekday: 'short',
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return dateStr;
  }
}

const languageFlag: Record<string, string> = {
  en: 'EN',
  ml: 'ML',
  hi: 'HI',
  ta: 'TA',
};

/** Single call card. Receives the active call + a way to dismiss locally. */
function CallCard({
  call,
  localElapsedBase,
  onDismiss,
}: {
  call: ActiveCall;
  localElapsedBase: number;
  onDismiss: (id: number) => void;
}) {
  // Derive a live-ticking elapsed time without re-fetching every second.
  // Use the server-provided elapsedSeconds as the baseline, then add the
  // client-side tick (localElapsedBase seconds since last fetch).
  const elapsed = call.elapsedSeconds + localElapsedBase;

  const isClient = !!call.clientId;

  return (
    <div className="w-80 bg-slate-900 border border-[#c9a962]/50 rounded-xl shadow-2xl overflow-hidden animate-in slide-in-from-right-4 duration-300">
      {/* Header: live pulse + elapsed + dismiss */}
      <div className="bg-gradient-to-r from-emerald-900/60 to-slate-900 px-4 py-2.5 flex items-center justify-between border-b border-slate-700">
        <div className="flex items-center gap-2">
          <div className="relative">
            <div className="w-2.5 h-2.5 bg-emerald-400 rounded-full" />
            <div className="absolute inset-0 w-2.5 h-2.5 bg-emerald-400 rounded-full animate-ping" />
          </div>
          <span className="text-sm font-semibold text-emerald-300">Live Call</span>
          {call.language && (
            <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-slate-600 text-slate-300">
              {languageFlag[call.language] || call.language.toUpperCase()}
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs font-mono text-emerald-300 tabular-nums">
            <Clock className="h-3 w-3 inline-block mr-1 -mt-0.5" />
            {formatElapsed(elapsed)}
          </span>
          <button
            onClick={() => onDismiss(call.id)}
            className="text-slate-500 hover:text-slate-200 transition-colors"
            title="Dismiss"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="p-4 space-y-3">
        {/* Caller identity */}
        <div className="flex items-start gap-3">
          <div className="p-2 bg-[#c9a962]/20 rounded-lg border border-[#c9a962]/30 shrink-0">
            <PhoneCall className="h-5 w-5 text-[#c9a962]" />
          </div>
          <div className="min-w-0 flex-1">
            {isClient ? (
              <>
                <div className="text-white font-semibold text-sm truncate flex items-center gap-1.5">
                  <User className="h-3.5 w-3.5 text-emerald-400 shrink-0" />
                  {call.clientName || call.callerName || 'Client'}
                </div>
                <Badge className="mt-0.5 bg-emerald-500/20 text-emerald-300 border-emerald-500/30 text-[10px]">
                  Existing Client
                </Badge>
              </>
            ) : (
              <>
                <div className="text-white font-semibold text-sm">
                  {call.callerName || 'Unknown caller'}
                </div>
                <Badge className="mt-0.5 bg-blue-500/20 text-blue-300 border-blue-500/30 text-[10px]">
                  {call.callerType === 'employee'
                    ? 'Employee'
                    : call.callerType === 'vendor'
                    ? 'Vendor'
                    : 'New caller'}
                </Badge>
              </>
            )}
            <div className="text-xs text-slate-400 font-mono mt-1">
              {formatCallerNumber(call.callerNumber)}
            </div>
          </div>
        </div>

        {/* Stage + AI route hint (hint only shown during first ~6s while ringing) */}
        {call.stage && (
          <div className="flex items-center gap-2 flex-wrap">
            <StageBadge stage={call.stage} />
            {call.elapsedSeconds <= 6 && (
              <span className="text-[11px] text-slate-400">
                Leena will route to {routeHint(call.stage)}
              </span>
            )}
          </div>
        )}

        {/* Wedding details (only for clients with a wedding row) */}
        {call.weddingId && (
          <div className="space-y-1.5 border-t border-slate-700 pt-3">
            {(call.brideName || call.groomName) && (
              <div className="flex items-center gap-1.5 text-xs text-slate-300">
                <User className="h-3 w-3 text-slate-500" />
                <span className="truncate">
                  {[call.brideName, call.groomName].filter(Boolean).join(' & ')}
                </span>
              </div>
            )}
            {call.weddingDate && (
              <div className="flex items-center gap-1.5 text-xs text-slate-300">
                <Calendar className="h-3 w-3 text-slate-500" />
                <span>{formatWeddingDate(call.weddingDate)}</span>
                {call.timeSlot && (
                  <span className="text-slate-500">· {call.timeSlot}</span>
                )}
              </div>
            )}
            {(call.weddingTitle || call.guestCount) && (
              <div className="flex items-center gap-1.5 text-xs text-slate-300">
                <Package className="h-3 w-3 text-slate-500" />
                {call.weddingTitle && <span className="truncate">{call.weddingTitle}</span>}
                {call.guestCount ? (
                  <span className="text-slate-500 shrink-0">· {call.guestCount} guests</span>
                ) : null}
              </div>
            )}
            {call.balanceAmount != null && Number(call.balanceAmount) > 0 && (
              <div className="flex items-center gap-1.5 text-xs text-amber-400">
                <Package className="h-3 w-3 text-amber-500" />
                <span>Balance due: ₹{Number(call.balanceAmount).toLocaleString('en-IN')}</span>
              </div>
            )}
            {call.weddingStatus && (
              <div className="flex items-center gap-1.5 text-xs">
                <Globe className="h-3 w-3 text-slate-500" />
                <Badge variant="outline" className="text-[10px] border-slate-600 text-slate-300 capitalize">
                  {call.weddingStatus}
                </Badge>
              </div>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-2 pt-1 flex-wrap">
          {call.clientId && (
            <Link href={`/admin/clients/${call.clientId}`}>
              <Button
                size="sm"
                className="bg-[#c9a962] hover:bg-[#e0c88a] text-slate-900 font-medium h-8 text-xs"
              >
                <ExternalLink className="h-3 w-3 mr-1" />
                Open client
              </Button>
            </Link>
          )}
          {/* No separate "Open wedding": the wedding dashboard IS the client page above —
              /admin/weddings/:id was never a route (it opened the 404 page). */}
          <Link href="/admin/voice-call-history">
            <Button
              size="sm"
              variant="outline"
              className={`border-slate-600 text-slate-300 hover:bg-slate-800 h-8 text-xs ${!call.clientId ? 'flex-1' : ''}`}
            >
              Call History
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}

/**
 * Where the card stack sits. `"default"`: the classic layouts' corner. `"aboveDock"`: the v2 manager
 * shell — below 1024px the phone dock (72px + 10px + the home-indicator inset) owns the bottom of the
 * screen, so the stack sits above it, where the client shell's paybar sits (94px + inset). At the
 * corner it covered Home / Menu until dismissed, and on an iPhone sat on the home indicator (R6-8).
 */
export type LiveCallPlacement = "default" | "aboveDock";
export const LIVE_CALL_STACK_CLASS: Record<LiveCallPlacement, string> = {
  default: "bottom-4",
  aboveDock: "bottom-[calc(94px+env(safe-area-inset-bottom))] lg:bottom-4",
};

export function LiveCallPopup({ placement = "default" }: { placement?: LiveCallPlacement } = {}) {
  // WebSocket-push driven: connects /ws/voice-events, rehydrates from
  // /active-calls on connect + on call.ringing/ended events. `enabled` is the
  // server-side runtime kill switch (VOICE_SCREEN_POP_ENABLED).
  const { calls, enabled } = useVoiceEvents();

  // Local client-side tick so elapsed time counts up smoothly between events.
  // Reset to 0 whenever the calls list changes so we realign with the
  // server-provided elapsedSeconds.
  const [tick, setTick] = useState(0);

  // IDs the user has explicitly dismissed — we don't re-show them even if
  // the call is still active (until page reload or the call actually ends).
  const [dismissed, setDismissed] = useState<Set<number>>(new Set());

  // Realign elapsed baseline whenever a fresh calls payload arrives.
  // Keyed on the id+elapsed signature so a new push resets the tick.
  const callsSignature = calls.map((c) => `${c.id}:${c.elapsedSeconds}`).join(',');
  useEffect(() => {
    setTick(0);
  }, [callsSignature]);
  useEffect(() => {
    const timer = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(timer);
  }, []);

  // Prune dismissed set: if a call has ended (no longer in the list),
  // remove it from the dismissed set so the next call with the same id
  // (row ID recycles only if autoincrement wraps, but pruning is tidy anyway)
  // isn't pre-dismissed.
  useEffect(() => {
    const activeIds = new Set(calls.map((c) => c.id));
    setDismissed((prev) => {
      let changed = false;
      const next = new Set<number>();
      for (const id of prev) {
        if (activeIds.has(id)) {
          next.add(id);
        } else {
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [callsSignature]);

  // Server-side kill switch — unmount globally when disabled.
  if (!enabled) return null;

  // Safety net: if a `call.ended` event is ever missed, drop a card that has been
  // live beyond MAX_CALL_SECONDS so it can't stay stuck on screen forever. Real
  // calls virtually never exceed this. (Review 2026-07-12.)
  const MAX_CALL_SECONDS = 15 * 60;
  const visibleCalls = calls.filter(
    (c) => !dismissed.has(c.id) && (c.elapsedSeconds + tick) < MAX_CALL_SECONDS,
  );

  // Hidden state: nothing to render at all when no active, non-dismissed calls.
  if (visibleCalls.length === 0) return null;

  const handleDismiss = (id: number) => {
    setDismissed((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  };

  return (
    <div
      className={`fixed ${LIVE_CALL_STACK_CLASS[placement]} right-4 z-[60] flex flex-col-reverse gap-2 pointer-events-none`}
      aria-live="polite"
    >
      {visibleCalls.map((call) => (
        <div key={call.id} className="pointer-events-auto">
          <CallCard call={call} localElapsedBase={tick} onDismiss={handleDismiss} />
        </div>
      ))}
    </div>
  );
}
